import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CloseDetailsGuard } from '@bci-web-core/core';
import { AppGuard } from './app.guard';
import { AddBuildingComponent } from './Components/add/add-building/add-building.component';
import { AddDcuComponent } from './Components/add/add-dcu/add-dcu.component';
import { AddLoadComponent } from './Components/add/add-load/add-load.component';
import { AddMeterComponent } from './Components/add/add-meter/add-meter.component';
// Add
import { AddNodeComponent } from './Components/add/add-node/add-node.component';
import { AddUserComponent } from './Components/add/add-user/add-user.component';
import { PeakDemandReportComponent } from './Components/export/peak-demand-report/peak-demand-report.component';
import { RealtimeDataComponent } from './Components/export/realtime-data/realtime-data.component';
import { HelpComponent } from './Components/help/help.component';
import { HomeComponent } from './Components/home/home.component';
import { LangChangeComponent } from './Components/lang-change/lang-change.component';
import { AirReportComponent } from './Components/report/air-report/air-report.component';
import { EnergyTreeExcelComponent } from './Components/report/energy-tree-excel/energy-tree-excel.component';
import { EqBenchmarkingComponent } from './Components/report/eq-benchmarking/eq-benchmarking.component';
import { GasReportComponent } from './Components/report/gas-report/gas-report.component';
import { ManageReportComponent } from './Components/report/manage-report/manage-report.component';
import { ManageSolarSchedulerComponent } from './Components/report/manage-solar-scheduler/manage-solar-scheduler.component';
import { SolarDailyReportComponent } from './Components/report/solar-daily-report/solar-daily-report.component';
import { SolarMonthlyReportComponent } from './Components/report/solar-monthly-report/solar-monthly-report.component';
import { WaterReportComponent } from './Components/report/water-report/water-report.component';
// Role-Management
import { RoleManagementComponent } from './Components/role-management/role-management.component';
// Energy Dashboard
import {
  DeepDiveComponent,
  EnergyComponent,
  EnergyDashboardComponent,
  EnergyTrendsComponent,
  LoadOnTreeComponent,
  MeterViewComponent,
  NotificationsComponent as NotificationsOnEngergyComponent,
  RealTimeComponent,
  SpecificEnergyComponent,
  TestbenchOverviewComponent,
  TransformerDashboardComponent,
  TransformerMonitoringComponent
} from './Components/site/energy-dashboard/index';
import { KpiBenchmarkingComponent } from './Components/site/kpi-benchmarking/kpi-benchmarking.component';
import { MachineMonitoringComponent } from './Components/site/machine-monitoring/machine-monitoring.component';
import { OverViewComponent } from './Components/site/over-view/over-view.component';
import { PlantNavigationComponent } from './Components/site/over-view/plant-navigation/plant-navigation.component';
import { UtilityConsumptionChartsComponent } from './Components/site/over-view/utility-consumptionCharts/utility-consumptionCharts.component';
// Site
import {
  ClickAndNavigationDetailsComponent,
  ConnectionDetailsComponent,
  DataAcquisitionLayoutComponent,
  ReportConfigurationComponent,
  SetupProjectComponent,
  ShiftDetailsComponent,
  SiteCreationComponent,
  SiteDetailComponent,
  SiteMasterComponent,
  TreeConfigurationComponent,
  WidgetConfigurationComponent
} from './Components/site/site-master/index';
import { ProcessAndLoadDetailsComponent } from './Components/site/site-master/process-and-load-details/process-and-load-details.component';
import { ArchitectureComponent } from './Components/site/solar-dashboard/architecture/architecture.component';
import { InverterViewComponent } from './Components/site/solar-dashboard/inverter-view/inverter-view.component';
import { NotificationsComponent } from './Components/site/solar-dashboard/notifications/notifications.component';
import { PlantOverviewComponent } from './Components/site/solar-dashboard/plant-overview/plant-overview.component';
// eslint-disable-next-line max-len
import { SolarLongTermTrendComponent } from './Components/site/solar-dashboard/plant-overview/solar-long-term-trend/solar-long-term-trend.component';
// Solar Dashboard
import { SolarDashboardComponent } from './Components/site/solar-dashboard/solar-dashboard.component';
import { UtilitiesComponent } from './Components/site/utilities/utilities.component';
import { LoginComponent } from './Components/user/login.component';
import { PostLoginComponent } from './Components/user/post-login/post-login.component';
import { UserManagementComponent } from './Components/user/user-management/user-management.component';
import { DcuInventoryComponent } from './Components/view/dcu-inventory/dcu-inventory.component';
import { LoadInventoryComponent } from './Components/view/load-inventory/load-inventory.component';
import { MeterInventoryComponent } from './Components/view/meter-inventory/meter-inventory.component';
import { AuthGuard } from './shared';
const routes: Routes = [
  { path: '', component: LoginComponent },
  { path: 'login', component: LoginComponent },
  { path: 'post-login', component: PostLoginComponent },
  {
    path: 'user-management',
    component: UserManagementComponent,
    canActivate: [AuthGuard]
  },
  { path: 'add-user', component: AddUserComponent, canActivate: [AuthGuard] },
  { path: 'add-node', component: AddNodeComponent, canActivate: [AuthGuard] },
  { path: 'add-dcu', component: AddDcuComponent, canActivate: [AuthGuard] },
  { path: 'add-meter', component: AddMeterComponent, canActivate: [AuthGuard] },
  { path: 'add-load', component: AddLoadComponent, canActivate: [AuthGuard] },
  {
    path: 'add-load/:loadId/:siteId',
    component: AddLoadComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'add-building',
    component: AddBuildingComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'role-management',
    component: RoleManagementComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'meter-inventory',
    component: MeterInventoryComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'load-inventory',
    component: LoadInventoryComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'dcu-inventory',
    component: DcuInventoryComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'site-master',
    component: SiteMasterComponent,
    children: [
      {
        path: ':id',
        component: SiteDetailComponent
      }
    ]
  },
  {
    path: 'setup-project',
    component: SetupProjectComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'site',
    component: HomeComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'over-view',
        component: OverViewComponent
      },
      {
        path: 'energy-dashboard',
        component: EnergyDashboardComponent,
        children: [
          {
            path: 'energy',
            component: EnergyComponent
          },
          {
            path: 'load-on-tree',
            component: LoadOnTreeComponent
          },
          {
            path: 'testbench-overview',
            component: TestbenchOverviewComponent
          },
          {
            path: 'real-time',
            component: RealTimeComponent
          },
          {
            path: 'specific-energy',
            component: SpecificEnergyComponent
          },
          {
            path: 'meter-view',
            component: MeterViewComponent
          },
          {
            path: 'transformer-monitoring',
            component: TransformerMonitoringComponent
          },
          {
            path: 'transformer-dashboard',
            component: TransformerDashboardComponent
          },
          {
            path: 'deep-dive',
            component: DeepDiveComponent
          },
          {
            path: 'notifications',
            component: NotificationsOnEngergyComponent
          }
        ]
      },
      {
        path: 'utilities',
        component: UtilitiesComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'solar-dashboard',
        component: SolarDashboardComponent,
        canActivate: [AuthGuard],
        children: [
          {
            path: 'plant-view',
            component: PlantOverviewComponent
          },
          {
            path: 'inverter-view',
            component: InverterViewComponent
          },
          {
            path: 'architecture',
            component: ArchitectureComponent
          },
          {
            path: 'notification',
            component: NotificationsComponent
          }
        ]
      },
      {
        path: 'machine-monitoring',
        component: MachineMonitoringComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'kpi-benchmarking',
        component: KpiBenchmarkingComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'solar-daily',
        component: SolarDailyReportComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'plant-navigation',
        component: PlantNavigationComponent,
        canActivate: [AuthGuard]
      }
    ]
  },
  {
    path: 'multi-step-form',
    component: SetupProjectComponent,
    canActivate: [AuthGuard],
    children: [
      { path: 'site-creation', component: SiteCreationComponent },
      { path: 'connection-details', component: ConnectionDetailsComponent },
      { path: 'shift-details', component: ShiftDetailsComponent },
      {
        path: 'process-and-load-details',
        component: ProcessAndLoadDetailsComponent
      },
      { path: 'widget-configuration', component: WidgetConfigurationComponent },
      { path: 'report-configuration', component: ReportConfigurationComponent },
      {
        path: 'data-acquisition-layout',
        component: DataAcquisitionLayoutComponent
      },
      { path: 'tree-configuration', component: TreeConfigurationComponent },
      {
        path: 'click-and-navigation-details',
        component: ClickAndNavigationDetailsComponent
      }
    ]
  },
  {
    path: 'solar-long-term-trend',
    component: SolarLongTermTrendComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'energy-long-term-trend',
    component: EnergyTrendsComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'utility-consumptionCharts',
    component: UtilityConsumptionChartsComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'energy-tree-excel',
    component: EnergyTreeExcelComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'water-report',
    component: WaterReportComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'air-report',
    component: AirReportComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'gas-report',
    component: GasReportComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'solar-daily/:type',
    component: SolarDailyReportComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'solar-monthly/:type',
    component: SolarMonthlyReportComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'manage-report',
    component: ManageReportComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'manage-solar-scheduler',
    component: ManageSolarSchedulerComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'help',
    component: HelpComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'sub-item-1',
    component: EqBenchmarkingComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'lang-change',
    component: LangChangeComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  //Export
  {
    path: 'realtime-data',
    component: RealtimeDataComponent,
    canActivate: [AppGuard, AuthGuard]
  },
  {
    path: 'peak-demand-report',
    component: PeakDemandReportComponent,
    canActivate: [AppGuard, AuthGuard]
  }
];
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [CloseDetailsGuard]
})
export class AppRoutingModule {}
